import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const REQUIREMENT_OPTIONS = [
  { value: 'A', label: 'A - Single Record' },
  { value: 'B', label: 'B - Total Records Per Day' },
  { value: 'C', label: 'C - Total Records Per Week' }
];

const AvailabilityCheck = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('availability_checks');
  const [viewItem, setViewItem] = useState(null);
  const [availabilityChecks, setAvailabilityChecks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCheck, setEditingCheck] = useState(null);

  useEffect(() => {
    loadAvailabilityChecks();
  }, []);

  const loadAvailabilityChecks = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/availability-checks`);
      setAvailabilityChecks(response.data || []);
    } catch (error) {
      console.error('Error loading availability checks:', error);
      toast.error('Failed to load availability checks');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (check = null) => {
    setEditingCheck(check);
    setIsDialogOpen(true);
  };

  const getRequirementLabel = (value) => {
    const option = REQUIREMENT_OPTIONS.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);

    const data = {
      availability_check_id: form.get('availability_check_id').toUpperCase(),
      description: form.get('description'),
      total_sales_requirement: form.get('total_sales_requirement'),
      total_delivery_requirement: form.get('total_delivery_requirement'),
      no_check: form.get('no_check') === 'on'
    };

    try {
      if (editingCheck) {
        await axios.put(`${BACKEND_URL}/api/availability-checks/${editingCheck.availability_check_id}`, {
          description: data.description,
          total_sales_requirement: data.total_sales_requirement,
          total_delivery_requirement: data.total_delivery_requirement,
          no_check: data.no_check
        });
        toast.success('Availability Check updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/availability-checks`, data);
        toast.success('Availability Check created successfully');
      }
      setIsDialogOpen(false);
      loadAvailabilityChecks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save availability check');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Availability Checks</h1>
            <p className="text-sm text-gray-500 mt-1">Manage availability check configurations</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Availability Checks</h2>
            {writable && (<Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Availability Check
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Check ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Sales Req</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Delivery Req</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">No Check</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {availabilityChecks.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-gray-500">
                        No availability checks found. Click &quot;Add Availability Check&quot; to create one.
                      </td>
                    </tr>
                  ) : (
                    availabilityChecks.map((check) => (
                      <tr key={check.availability_check_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm font-semibold">{check.availability_check_id}</td>
                        <td className="py-3 px-4 text-sm">{check.description}</td>
                        <td className="py-3 px-4 text-center">
                          <span className="px-2 py-1 text-xs rounded bg-orange-100 text-orange-700 font-mono">
                            {getRequirementLabel(check.total_sales_requirement)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="px-2 py-1 text-xs rounded bg-orange-100 text-orange-700 font-mono">
                            {getRequirementLabel(check.total_delivery_requirement)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 text-xs rounded ${check.no_check ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                            {check.no_check ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <RecordActions doctype="availability_checks" onView={() => setViewItem(check)} onEdit={() => handleOpenDialog(check)} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingCheck ? 'Edit Availability Check' : 'Add Availability Check'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="availability_check_id">Availability Check ID * (2 characters)</Label>
                  <Input
                    id="availability_check_id"
                    name="availability_check_id"
                    defaultValue={editingCheck?.availability_check_id || ''}
                    placeholder="e.g., 01, 02, KP"
                    maxLength={2}
                    minLength={2}
                    pattern="[A-Za-z0-9]{2}"
                    title="Must be exactly 2 alphanumeric characters"
                    disabled={!!editingCheck}
                    className="uppercase"
                    required
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 2);
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Must be exactly 2 alphanumeric characters</p>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Input
                    name="description"
                    defaultValue={editingCheck?.description || ''}
                    placeholder="e.g., ATP Check with Sales"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Total Sales Requirement *</Label>
                  <select
                    name="total_sales_requirement"
                    defaultValue={editingCheck?.total_sales_requirement || 'A'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    {REQUIREMENT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Total Delivery Requirement *</Label>
                  <select
                    name="total_delivery_requirement"
                    defaultValue={editingCheck?.total_delivery_requirement || 'A'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    {REQUIREMENT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="no_check"
                    name="no_check"
                    defaultChecked={editingCheck?.no_check || false}
                    className="w-4 h-4"
                  />
                  <label htmlFor="no_check" className="text-sm text-gray-700">
                    No Check (Disable availability checking)
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingCheck ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default AvailabilityCheck;
